import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import offlineManager from '../../utils/offlineManager';

export default function MobileSettings() {
    const { user } = useAuth();
    const { showToast, showConfirm } = useUI();
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [swRegistration, setSwRegistration] = useState(null);

    // Sync States
    const [syncState, setSyncState] = useState({
        active: false,
        total: 0,
        current: 0,
        label: ''
    });

    // Queue States
    const [queue, setQueue] = useState({
        movements: [],
        products: [],
        receptions: []
    });
    const [loadingQueue, setLoadingQueue] = useState(true);

    const refreshQueue = useCallback(async () => {
        setLoadingQueue(true);
        try {
            const [m, p, r] = await Promise.all([
                offlineManager.getQueuedMovements(),
                offlineManager.getQueuedProducts(),
                offlineManager.getQueuedReceptions()
            ]);
            setQueue({ movements: m, products: p, receptions: r });
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingQueue(false);
        }
    }, []);

    useEffect(() => {
        const handleStatus = () => setIsOnline(navigator.onLine);
        window.addEventListener('online', handleStatus);
        window.addEventListener('offline', handleStatus);

        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistration().then(setSwRegistration);
        }

        refreshQueue();

        return () => {
            window.removeEventListener('online', handleStatus);
            window.removeEventListener('offline', handleStatus);
        };
    }, [refreshQueue]);

    const handleDeepSync = async () => {
        if (!user?.companyId) return;
        if (!navigator.onLine) {
            showToast("Connexion internet requise pour la synchronisation", "error");
            return;
        }

        setSyncState({ active: true, total: 100, current: 5, label: 'Préparation...' });

        try {
            // 1. Sync Produits
            setSyncState(s => ({ ...s, label: 'Téléchargement articles...', current: 10 }));
            const prodRes = await fetch(`/api/products?companyId=${user.companyId}&limit=1000`);
            let products = [];
            if (prodRes.ok) {
                const data = await prodRes.json();
                products = Array.isArray(data) ? data : (data.products || []);
                await offlineManager.saveProducts(products);
            }

            // 2. Pre-cache images (les 50 premières avec image)
            setSyncState(s => ({ ...s, label: 'Mise en cache des photos...', current: 40 }));
            const productsWithImages = products.filter(p => p.imageUrl).slice(0, 50);
            for (let i = 0; i < productsWithImages.length; i++) {
                const p = productsWithImages[i];
                try {
                    // On fetch l'image : le Service Worker v4 l'interceptera et la mettra en cache
                    await fetch(p.imageUrl);
                } catch (e) { }
                setSyncState(s => ({ ...s, current: 40 + Math.floor((i / productsWithImages.length) * 20) }));
            }

            // 3. Sync Commandes & Détails
            setSyncState(s => ({ ...s, label: 'Téléchargement commandes...', current: 65 }));
            const ordersRes = await fetch(`/api/purchase-orders?companyId=${user.companyId}&limit=30`);
            if (ordersRes.ok) {
                const data = await ordersRes.json();
                const orders = data.orders || [];

                for (let i = 0; i < orders.length; i++) {
                    await fetch(`/api/purchase-orders/${orders[i].id}`).catch(() => null);
                    setSyncState(s => ({ ...s, current: 65 + Math.floor((i / orders.length) * 25) }));
                }
            }

            // 4. Warehouses
            setSyncState(s => ({ ...s, label: 'Finalisation...', current: 95 }));
            await Promise.all([
                fetch(`/api/warehouses?companyId=${user.companyId}`),
                fetch(`/api/companies/${user.companyId}`)
            ]);

            setSyncState(s => ({ ...s, current: 100, label: 'Terminé !' }));
            showToast("Synchronisation complète terminée", "success");
            setTimeout(() => setSyncState(s => ({ ...s, active: false })), 2000);
        } catch (err) {
            console.error("Deep sync failed", err);
            showToast("Erreur de synchronisation", "error");
            setSyncState(s => ({ ...s, active: false }));
        }
    };

    const handleSyncQueue = async () => {
        if (!navigator.onLine) {
            showToast("Vous devez être en ligne pour envoyer les données", "error");
            return;
        }

        const totalItems = queue.movements.length + queue.products.length + queue.receptions.length;
        if (totalItems === 0) return;

        setSyncState({ active: true, total: totalItems, current: 0, label: 'Initialisation de l\'envoi...' });
        let processed = 0;
        let errors = 0;

        const delay = () => new Promise(r => setTimeout(r, 400)); // Petit délai pour que l'œil humain voit le progrès

        try {
            const productMap = {}; // tempId (string) -> realId (int)

            // 1. Sync Products (MUST BE FIRST to get real IDs)
            for (const p of queue.products) {
                setSyncState(s => ({ ...s, label: `Traitement produit : ${p.sku}...` }));
                try {
                    let finalProduct = { ...p };

                    // Upload local image if exists
                    if (p.capturedImage) {
                        console.log(`[Sync] Tentative de téléversement photo pour ${p.sku}`, p.capturedImage);
                        setSyncState(s => ({ ...s, label: `Téléversement photo : ${p.sku}...` }));

                        const uploadFormData = new FormData();
                        const file = p.capturedImage instanceof File
                            ? p.capturedImage
                            : new File([p.capturedImage], `offline_photo_${p.sku}.jpg`, { type: 'image/jpeg' });

                        uploadFormData.append('image', file);
                        const uploadRes = await fetch('/api/upload/product-image', { method: 'POST', body: uploadFormData });

                        if (uploadRes.ok) {
                            const uploadData = await uploadRes.json();
                            finalProduct.imageUrl = uploadData.imageUrl;
                        }
                        delete finalProduct.capturedImage;
                    }

                    const res = await fetch('/api/products', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(finalProduct)
                    });

                    if (res.ok) {
                        const newProd = await res.json();
                        productMap[p.tempId] = newProd.id;
                        await offlineManager.removeQueuedProduct(p.tempId);
                        processed++;
                    } else {
                        errors++;
                    }
                } catch (err) {
                    errors++;
                }
                setSyncState(s => ({ ...s, current: processed + errors }));
                await delay();
            }

            // 2. Sync Movements (Now we have the real product IDs)
            for (const m of queue.movements) {
                setSyncState(s => ({ ...s, label: `Envoi mouvement : ${m.sku}...` }));
                try {
                    let finalMovement = { ...m };

                    // If this movement belongs to a newly created product, use the real ID
                    if (m.tempProductId && productMap[m.tempProductId]) {
                        finalMovement.productId = productMap[m.tempProductId];
                    }

                    if (!finalMovement.productId) {
                        console.error("[Sync] Erreur : Pas d'ID produit pour le mouvement", m);
                        errors++;
                        continue;
                    }

                    const res = await fetch('/api/stock-movements', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(finalMovement)
                    });
                    if (res.ok) {
                        await offlineManager.removeMovement(m.tempId);
                        processed++;
                    } else {
                        errors++;
                    }
                } catch (err) {
                    errors++;
                }
                setSyncState(s => ({ ...s, current: processed + errors }));
                await delay();
            }

            // 3. Sync Receptions
            for (const r of queue.receptions) {
                setSyncState(s => ({ ...s, label: `Réception commande : ${r.reference}...` }));
                try {
                    const res = await fetch(`/api/purchase-orders/${r.orderId}/receive`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(r)
                    });
                    if (res.ok) {
                        await offlineManager.removeReception(r.tempId);
                        processed++;
                    } else {
                        errors++;
                    }
                } catch (err) {
                    errors++;
                }
                setSyncState(s => ({ ...s, current: processed + errors }));
                await delay();
            }

            if (errors > 0) {
                showToast(`${processed} synchronisés, ${errors} échecs.`, "info");
            } else {
                showToast(`${processed} éléments synchronisés avec succès`, "success");
            }
            refreshQueue();
        } catch (e) {
            console.error("Global sync error", e);
            showToast("Erreur critique pendant la synchronisation", "error");
        } finally {
            setSyncState(s => ({ ...s, label: processed === totalItems ? 'Terminé !' : 'Synchro terminée' }));
            setTimeout(() => setSyncState(s => ({ ...s, active: false })), 1000);
        }
    };

    const removeItem = (type, id) => {
        showConfirm({
            title: "Supprimer cet élément ?",
            message: "Cette action est irréversible et annulera la saisie.",
            onConfirm: async () => {
                if (type === 'movement') await offlineManager.removeMovement(id);
                if (type === 'product') await offlineManager.removeQueuedProduct(id);
                if (type === 'reception') await offlineManager.removeReception(id);
                refreshQueue();
                showToast("Élément supprimé", "info");
            }
        });
    };

    const handleUpdateApp = async () => {
        if (!swRegistration) {
            showToast("Service Worker non trouvé", "info");
            return;
        }
        showToast("Vérification...", "info");
        try {
            const update = await swRegistration.update();
            if (!update || !update.waiting) {
                showToast("Déjà à jour", "success");
            } else {
                showConfirm({
                    title: "Mise à jour !",
                    message: "Redémarrer l'application ?",
                    onConfirm: () => window.location.reload()
                });
            }
        } catch (e) {
            showToast("Erreur", "error");
        }
    };

    const totalQueueCount = queue.movements.length + queue.products.length + queue.receptions.length;

    return (
        <div className="space-y-6 pb-24 max-w-lg mx-auto p-4">
            <header className="flex items-center gap-3">
                <span className="material-symbols-outlined text-primary text-3xl">settings</span>
                <h1 className="text-2xl font-black">Configuration</h1>
            </header>

            {/* Sync Progress Overlay */}
            {syncState.active && (
                <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-[100] flex items-center justify-center p-6">
                    <div className="bg-white dark:bg-slate-800 w-full rounded-3xl p-6 shadow-2xl space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="font-black text-slate-800 dark:text-white uppercase tracking-wider">{syncState.label}</h3>
                            <span className="text-primary font-black">{Math.round((syncState.current / syncState.total) * 100)}%</span>
                        </div>
                        <div className="h-4 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden border border-slate-200 dark:border-slate-600">
                            <div
                                className="h-full bg-primary transition-all duration-300 rounded-full shadow-inner shadow-black/10"
                                style={{ width: `${(syncState.current / syncState.total) * 100}%` }}
                            ></div>
                        </div>
                        <p className="text-center text-xs text-slate-400 font-bold uppercase tracking-widest">Ne fermez pas l'application</p>
                    </div>
                </div>
            )}

            {/* Network & Offline Queue */}
            <div className={`p-4 rounded-2xl border ${isOnline ? 'bg-emerald-50 border-emerald-100 text-emerald-700' : 'bg-rose-50 border-rose-100 text-rose-700'
                }`}>
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined">{isOnline ? 'wifi' : 'wifi_off'}</span>
                        <span className="font-black text-sm uppercase">Mode {isOnline ? 'Connecté' : 'Hors-ligne'}</span>
                    </div>
                    {totalQueueCount > 0 && isOnline && (
                        <button
                            onClick={handleSyncQueue}
                            className="bg-primary text-white px-4 py-2 rounded-xl text-xs font-black shadow-lg shadow-primary/30 animate-bounce"
                        >
                            SYNC ATTENTE ({totalQueueCount})
                        </button>
                    )}
                </div>

                {totalQueueCount > 0 ? (
                    <div className="bg-white/50 rounded-xl p-3 space-y-2">
                        <p className="text-xs font-bold uppercase tracking-wider opacity-70">File d'attente ({totalQueueCount} éléments)</p>
                        <ul className="space-y-1 max-h-40 overflow-y-auto">
                            {queue.receptions.map(r => (
                                <li key={r.tempId} className="flex items-center justify-between bg-white/40 p-2 rounded-lg text-[11px]">
                                    <div className="flex flex-col">
                                        <span className="font-bold">📤 Réception : {r.reference}</span>
                                        <span className="opacity-60">{r.items.length} articles</span>
                                    </div>
                                    <button onClick={() => removeItem('reception', r.tempId)} className="text-rose-500">
                                        <span className="material-symbols-outlined text-sm">delete</span>
                                    </button>
                                </li>
                            ))}
                            {queue.movements.map(m => (
                                <li key={m.tempId} className="flex items-center justify-between bg-white/40 p-2 rounded-lg text-[11px]">
                                    <div className="flex flex-col">
                                        <span className="font-bold">📦 Mouvement : {m.sku}</span>
                                        <span className="opacity-60">Qté: {m.quantity} vers {m.type}</span>
                                    </div>
                                    <button onClick={() => removeItem('movement', m.tempId)} className="text-rose-500">
                                        <span className="material-symbols-outlined text-sm">delete</span>
                                    </button>
                                </li>
                            ))}
                            {queue.products.map(p => (
                                <li key={p.tempId} className="flex items-center justify-between bg-white/40 p-2 rounded-lg text-[11px]">
                                    <div className="flex flex-col">
                                        <div className="flex items-center gap-1.5">
                                            <span className="font-bold">✨ Nouveau : {p.sku}</span>
                                            {p.capturedImage && (
                                                <span className="material-symbols-outlined text-emerald-500 text-[14px]" title="Avec photo">image</span>
                                            )}
                                        </div>
                                        <span className="opacity-60">{p.name}</span>
                                    </div>
                                    <button onClick={() => removeItem('product', p.tempId)} className="text-rose-500">
                                        <span className="material-symbols-outlined text-sm">delete</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : (
                    <p className="text-[11px] font-bold uppercase opacity-50 text-center">Aucune donnée en attente</p>
                )}
            </div>

            {/* Sync Action */}
            <section className="bg-white dark:bg-slate-900 p-5 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
                <div className="flex items-center gap-3">
                    <span className="material-symbols-outlined text-primary">cloud_download</span>
                    <div>
                        <h2 className="font-black text-slate-800 dark:text-white uppercase tracking-tight">Vider / Rafraîchir local</h2>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Pré-charge produits, photos et commandes</p>
                    </div>
                </div>
                <button
                    onClick={handleDeepSync}
                    disabled={syncState.active || !isOnline}
                    className="w-full h-14 bg-primary text-white rounded-2xl font-black flex items-center justify-center gap-3 shadow-lg shadow-primary/20 active:scale-95 transition-all disabled:opacity-50"
                >
                    <span className={`material-symbols-outlined ${syncState.active ? 'animate-spin' : ''}`}>
                        {syncState.active ? 'sync' : 'auto_mode'}
                    </span>
                    TÉLÉCHARGER TOUTES LES DONNÉES
                </button>
            </section>

            {/* Update App */}
            <section className="bg-slate-900 text-white p-5 rounded-3xl border border-slate-800 shadow-xl space-y-4">
                <div className="flex items-center gap-3">
                    <span className="material-symbols-outlined text-primary">rocket_launch</span>
                    <div>
                        <h2 className="font-black uppercase tracking-tight">Version du Logiciel</h2>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">v4.0.6 - Stockizio Mobile</p>
                    </div>
                </div>
                <button
                    onClick={handleUpdateApp}
                    className="w-full h-12 bg-white/10 hover:bg-white/20 text-white rounded-xl font-black flex items-center justify-center gap-3 transition-all border border-white/10"
                >
                    <span className="material-symbols-outlined text-sm">update</span>
                    VÉRIFIER UNE MISE À JOUR
                </button>
            </section>

            {/* Danger Zone */}
            <section className="pt-4">
                <button
                    onClick={() => {
                        showConfirm({
                            title: "⚠️ RÉINITIALISER ?",
                            message: "Ceci videra TOUT le cache de l'app (y compris les photos). Utile en cas de gros bug.",
                            onConfirm: async () => {
                                const names = await caches.keys();
                                await Promise.all(names.map(n => caches.delete(n)));
                                window.location.reload();
                            }
                        })
                    }}
                    className="w-full py-4 text-rose-500 text-[10px] font-black uppercase tracking-[0.2em] border-2 border-dashed border-rose-200 rounded-2xl"
                >
                    Reset complet de l'application
                </button>
            </section>
        </div>
    );
}
